import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider } from "@mui/material";
import {
	IconComponent,
	CustomIconButton,
	icon,
	CustomPopOver,
	CustomButton,
	CustomTooltip,
	VersionComparisonPanel,
} from "../../../../../../../../common/components";
import { ApiService, CollabService } from "../../../../../../../../api";
import { translate } from "../../../../../../../../common/providers";
import styles from "./InformationsDeletedOverlay.module.css";
import { dangerousSetInnerHTML } from "../../../../../../../../common/utils";
import { updateNode } from "../../../../../../slice/pdf/pdf-slice";
import { setSelectedSearch } from "../../../../../../slice/document/document-slice";
import { checkHardInformation } from "../../../../../../utils/utils";
import { SOURCE_TYPES } from "../../../../../../../../common/constants";
import { DOCUMENT_STATUSES } from "../../../../../../constants/constants";

const TOPGAP = -16;

function VersionDeletedText({ className, text }) {
	return dangerousSetInnerHTML({ className, text });
}

export default function InformationsDeletedOverlay({
	height,
	hoveredWarning,
	deletedData,
	metadata,
	priority,
	onSetPriority,
	setHoveredWarning,
}) {
	const dispatch = useDispatch();
	const infosV1 = deletedData?.deletedInfosIds;
	const v2Id = deletedData?.informationId;
	const v2Rectangles = deletedData?.rectangles;
	const [open, setOpen] = useState(false);
	const [top, setTop] = useState(0);
	const [comparisonOpen, setComparisonOpen] = useState(false);
	const [previousInfoDetails, setPreviousInfoDetails] = useState(null);
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const isSelecting = useSelector(({ srDocument }) => srDocument.isSelecting);
	const documentInfo = useSelector(({ srDocument }) => srDocument);
	const previousVersionId = useSelector(({ srDocument }) => srDocument.previousVersionId);
	const previousVersionName = useSelector(({ srDocument }) => srDocument.previousVersionName);
	const isArchived = useSelector(({ srDocument }) => srDocument.status === DOCUMENT_STATUSES.ARCHIVED);
	const cancelTokenSourceRef = useRef(null);
	const isContested = useMemo(
		() =>
			metadata?.some(
				(md) =>
					md.infoVersionStatus !== "IDENTICAL" &&
					v2Rectangles?.[0] &&
					Math.abs(
						((md.rectangle?.y1 + md.rectangle?.y2) / 2 - (v2Rectangles[0].y1 + v2Rectangles[0].y2) / 2) *
							height
					) < 35
			),
		[metadata, v2Rectangles, height]
	);

	useEffect(() => {
		if (Array.isArray(v2Rectangles) && v2Rectangles.length > 0) {
			setTop(height * ((v2Rectangles[0].y1 + v2Rectangles[0].y2) / 2));
		}
	}, [height, v2Rectangles]);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	useEffect(() => {
		if (hoveredWarning !== v2Id) {
			setOpen(false);
		}
	}, [hoveredWarning, v2Id]);

	useEffect(() => {
		const handleScroll = () => {
			if (open) {
				setOpen(false);
			}
		};

		window.addEventListener("scroll", handleScroll, true);

		return () => {
			window.removeEventListener("scroll", handleScroll, true);
		};
	}, [open]);

	const getDeletedData = (documentId, informationsId) => {
		const stringInfos = [];
		CollabService.getListStringContent({ documentId }, informationsId, cancelTokenSourceRef.current.token)
			.then((data) => {
				data.map((a, i) => stringInfos.push({ id: informationsId[i], value: a.value }));
				dispatch(
					updateNode({
						mode: "deletedData",
						pageConditions: { page: deletedData.pageStart },
						informationId: deletedData.informationId,
						updates: [{ property: "deletedText", value: stringInfos }],
					})
				);
			})
			.catch((err) => console.error(err));
	};
	useEffect(
		() => {
			if (!deletedData?.deletedText && open) {
				getDeletedData(docId, infosV1);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch, open]
	);
	useEffect(() => {
		if (priority !== "delete") {
			setOpen(false);
		}
	}, [priority]);
	const handleMouseEnter = () => {
		if (!isSelecting) {
			setHoveredWarning(v2Id);
			setOpen(true);
			onSetPriority("delete");
		}
	};

	const handleClose = (e) => {
		e?.stopPropagation();
		setOpen(false);
	};

	const contents = {
		text: deletedData?.deletedText,
		title: translate(
			deletedData?.deletedText?.length > 1
				? "smart-review.version-icon.version-deleted-contents"
				: "smart-review.version-icon.version-deleted-content",
			{ count: deletedData?.deletedText?.length }
		),
	};
	const handleRedirect = (e) => {
		e?.stopPropagation();
		handleClose();

		const rightInfoId = Array.isArray(infosV1) && infosV1.length > 0 ? infosV1[0] : null;
		setPreviousInfoDetails({
			previousInfoId: rightInfoId,
			previousPage: deletedData?.pageStart || 1,
		});
		setComparisonOpen(true);
	};

	const handleCloseComparison = () => {
		setComparisonOpen(false);
		setPreviousInfoDetails(null);
	};

	const handleOpenSidePanel = () => {
		if (!isArchived) {
			const isRequirement = checkHardInformation(deletedData);
			handleClose();
			dispatch(
				setSelectedSearch({
					infoId: deletedData.informationId,
					pageStart: deletedData.pageStart,
					x1: deletedData.rectangles?.[0]?.x1,
					x2: deletedData.rectangles?.[0]?.x2,
					y1: deletedData.rectangles?.[0]?.y1,
					y2: deletedData.rectangles?.[0]?.y2,
					openSidePanel: true,
					source: SOURCE_TYPES.VERSIONING,
					isRequirement,
				})
			);
		}
	};

	return (
		<>
			<div
				key={v2Id}
				className={styles.versionIcon}
				data-contested={isContested}
				role="presentation"
				style={{
					top: top + TOPGAP,
				}}
				type="pointer"
				onClick={handleOpenSidePanel}
				onMouseEnter={handleMouseEnter}
			>
				<CustomPopOver
					content={
						<>
							<div className={styles.content}>
								<div className={styles.modal__header}>
									<div className={styles.modal__title}>
										<span>{contents.title}</span>
										<CustomTooltip title={translate("smart-review.version-info-icon.deleted")}>
											<span>
												<IconComponent
													className={styles.infoIcon}
													icon={icon.faInfoCircle}
													size="sm"
												/>
											</span>
										</CustomTooltip>
									</div>
									<CustomIconButton
										icon={icon.faCopy}
										size="xs"
										tooltip={translate("smart-review.version-icon.redirection")}
										variant="outlined"
										onClick={handleRedirect}
									/>
								</div>
								<div className={styles.content__body}>
									{deletedData?.deletedText &&
										contents?.text.map((text, i) => (
											<div key={text?.id}>
												{i !== 0 && <Divider />}
												<VersionDeletedText
													className={styles.deletedContent}
													text={text?.value}
												/>
											</div>
										))}
								</div>
								{!isArchived && (
									<div className={styles.openPanelBtn}>
										<CustomButton
											color="secondary"
											size="sm"
											variant="outlined"
											onClick={handleOpenSidePanel}
										>
											{translate("smart-review.version-modal.open-panel")}
										</CustomButton>
									</div>
								)}
							</div>
						</>
					}
					open={open}
					paperClassName={styles.paperRoot}
					placement="right-start"
					onClose={handleClose}
				>
					<span role="presentation" onClick={handleOpenSidePanel}>
						<IconComponent
							className={styles.deletedIcon}
							color="var(--red-500)"
							icon={icon.faMinusCircle}
							size="lg"
						/>
					</span>
				</CustomPopOver>
			</div>
			<VersionComparisonPanel
				enableLeftApiCall
				currentDocId={docId}
				currentDocName={documentInfo?.documentName}
				enableRightApiCall={false}
				initialLeftInfoId={null}
				initialLeftPage={previousInfoDetails?.previousPage || 1}
				initialRightInfoId={previousInfoDetails?.previousInfoId}
				initialRightPage={previousInfoDetails?.previousPage || 1}
				open={comparisonOpen}
				previousDocId={previousVersionId}
				previousDocName={previousVersionName || documentInfo?.previousVersionName}
				projectId={documentInfo?.projectId}
				onClose={handleCloseComparison}
			/>
		</>
	);
}
